import * as React from "react"
import type { ReactElement, KeyboardEvent } from "react"
import { Building2 } from "lucide-react"
import { useTheme } from "next-themes"

import { resolveConnectBrokerLogoUrl } from "@/components/Broker/brokerWizardLogos"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { cn } from "@/lib/utils"

const OTHER_BROKER_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]"

const OTHER_BROKER_DESC_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500"

export type BrokerChoiceCardProps = {
  brokerName: string
  displayName: string
  selected?: boolean
  connected?: boolean
  /** Text card for custom CSV / broker not listed (no logo). */
  variant?: "logo" | "other"
  /** When false, renders a static selected tile (no click / keyboard select). */
  interactive?: boolean
  onSelect?: () => void
}

export function BrokerChoiceCard({
  brokerName,
  displayName,
  selected = false,
  connected = false,
  variant = "logo",
  interactive = true,
  onSelect,
}: BrokerChoiceCardProps): ReactElement {
  const { resolvedTheme } = useTheme()
  const logoUrl =
    variant === "other"
      ? null
      : resolveConnectBrokerLogoUrl(brokerName, { dark: resolvedTheme === "dark" })

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!interactive || !onSelect) return
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      onSelect()
    }
  }

  return (
    <div
      role={interactive ? "button" : "img"}
      tabIndex={interactive ? 0 : undefined}
      data-broker-choice-card=""
      data-broker-choice-other={variant === "other" ? "" : undefined}
      data-selected={selected || !interactive ? "true" : "false"}
      aria-pressed={interactive ? selected : undefined}
      aria-label={
        variant === "other"
          ? connected
            ? "Other broker, already connected. Import via CSV or manually using our generic template."
            : "Broker not listed? No problem! Import via CSV or manually using our generic template."
          : interactive
            ? connected
              ? `${displayName}, already connected`
              : `Select ${displayName}`
            : displayName
      }
      onClick={interactive && onSelect ? onSelect : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
      className={cn(
        "flex min-h-[72px] w-full rounded-[6px] p-3 outline-none",
        variant === "other"
          ? "items-start justify-start"
          : "items-center justify-center",
        interactive && "focus-visible:ring-2 focus-visible:ring-[color:var(--brand-tropical-surf)]/40",
        !interactive && "pointer-events-none cursor-default"
      )}
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          aria-hidden
          className="pointer-events-none h-10 w-full max-w-[140px] object-contain"
          decoding="async"
        />
      ) : variant === "other" ? (
        <Field className="pointer-events-none w-full min-w-0 gap-1 text-left">
          <FieldLabel className={OTHER_BROKER_LABEL_CLASS}>
            Broker not listed? No problem!
          </FieldLabel>
          <FieldDescription className={OTHER_BROKER_DESC_CLASS}>
            Import via CSV or manually using our generic template.
          </FieldDescription>
        </Field>
      ) : (
        <div
          className="pointer-events-none flex size-14 items-center justify-center rounded-[6px] border border-border bg-muted/30"
          aria-hidden
        >
          <Building2 className="size-6 text-muted-foreground" />
        </div>
      )}
    </div>
  )
}
